import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, groupBy, map, mergeMap, switchMap } from 'rxjs/operators';
import { PREPAYMENT_PLUGINS_USER_BALANCE_API_ACTIONS } from '@store/prepayment/prepayment.action';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { PrepaymentPluginApiService } from '@core/services/api/plugin/prepayment-plugin-api.service';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { combineLatest, EMPTY, of } from 'rxjs';
import { PrepaymentUserBalance } from '@core/models/prepayment.model';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { ShopsService } from '@core/services/shops/shops.service';

@Injectable({
  providedIn: 'root',
})
export class PrepaymentEffects {
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly prepaymentPluginApiService = inject(PrepaymentPluginApiService);
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly shopsService = inject(ShopsService);

  constructor(private _actions: Actions) {}

  // Last Update
  fetchPrepaymentUserBalanceIfNeeded = createEffect(
    () => {
      return this._actions.pipe(
        ofType(PREPAYMENT_PLUGINS_USER_BALANCE_API_ACTIONS.needToDisplayUserBalance),
        switchMap(({ shopId }) => {
          const prepaymentPluginType =
            this.prepaymentSelectorService.selectPrepaymentPluginTypeByShop(shopId)();
          if (!prepaymentPluginType) {
            return this.shopsService.loadShopById$(shopId).pipe(
              switchMap(() =>
                combineLatest({
                  pluginType:
                    this.prepaymentSelectorService.selectPrepaymentPluginTypeByShop$(shopId),
                  shopId: of(shopId),
                  isAuthenticated: this.authStoreSelector.selectUserIsLoaded$,
                }),
              ),
            );
          }

          return combineLatest({
            pluginType: of(prepaymentPluginType),
            isAuthenticated: this.authStoreSelector.selectUserIsLoaded$,
            shopId: of(shopId),
          });
        }),
        groupBy(({ shopId }) => shopId),
        mergeMap(group$ =>
          group$.pipe(
            exhaustMap(({ pluginType, isAuthenticated, shopId }) => {
              if (!pluginType || !isAuthenticated) {
                return EMPTY;
              }
              return statedStream(
                this.prepaymentPluginApiService.loadUserBalance$({ pluginType, shopId }),
              ).pipe(
                map(data => {
                  if (data.isLoading) {
                    return PREPAYMENT_PLUGINS_USER_BALANCE_API_ACTIONS.fetching({
                      shopId,
                    });
                  }
                  if (data.hasError) {
                    return PREPAYMENT_PLUGINS_USER_BALANCE_API_ACTIONS.fetchingFailure({
                      error: data.error,
                      shopId,
                    });
                  }
                  return PREPAYMENT_PLUGINS_USER_BALANCE_API_ACTIONS.fetchingSuccess({
                    shopId,
                    ...(data.result as PrepaymentUserBalance),
                  });
                }),
              );
            }),
          ),
        ),
      );
    },
    {
      dispatch: true,
    },
  );
}
